// MaintenanceModal.jsx (новая версия под GlobalSettings)
import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Button,
    Form,
    Row,
    Col,
    Alert,
    Spinner,
    Badge,
    Card,
} from "react-bootstrap";
import {
    fetchGlobalSettings,
    updateGlobalSettings,
} from "../../http/bookAPI";

// те же поля, что и в модели
const INT_FIELDS = [
    "update_duration_minutes",
    "free_user_max_active_ass",
    "essential_user_max_active_ass",
    "premium_user_max_active_ass",
    "maximum_user_max_active_ass",
    "max_assignments_per_10_days",
    "max_assignments_per_30_days",
    "max_reported_assignments",
    "max_reading_assignments",
    "max_assignments_to_post_per_book",
    "welcome_bonus_ttl_min",
];
const BOOL_FIELDS = ["update_active", "staff_message_active", "dashboard_rdp_access_visible", "spa_update_notice_active"];
const STR_FIELDS = ["update_message", "staff_message_dashboard", "spa_update_notice_version", "spa_update_notice_mode", "spa_update_notice_message"];
// + режим сохранения промо-конфига (merge/replace)
const PROMO_MODES = ['merge', 'replace'];

const MaintenanceModal = ({ show, handleClose }) => {
    const [orig, setOrig] = useState(null);   // то, что пришло с бэка
    const [form, setForm] = useState(null);   // редактируемые значения
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // загрузка при открытии
    useEffect(() => {
        if (!show) return;
        let mounted = true;
        (async () => {
            setLoading(true);
            setError("");
            setSuccess("");
            try {
                const data = await fetchGlobalSettings();
                if (!mounted) return;
                setOrig(data);
                setForm(toFormState(data));
            } catch (e) {
                // 404 => настроек нет, подготовим пустую форму (попросим заполнить)
                setOrig(null);
                setForm({
                    update_duration_minutes: "",
                    staff_message_dashboard: "Staff Message Here",
                    update_message:
                        "The Service Under Maintenance Now. Please, wait for a while",
                    staff_message_active: false,
                    dashboard_rdp_access_visible: false,
                    spa_update_notice_active: false,
                    spa_update_notice_version: "",
                    spa_update_notice_mode: "modal",
                    spa_update_notice_message: "Virtalibry has just been updated. Refresh this page to continue with the latest version.",
                    update_active: false,
                    free_user_max_active_ass: "",
                    essential_user_max_active_ass: "",
                    premium_user_max_active_ass: "",
                    maximum_user_max_active_ass: "",
                    max_assignments_per_10_days: "",
                    max_assignments_per_30_days: "",
                    max_reported_assignments: "",
                    max_reading_assignments: "",
                    max_assignments_to_post_per_book: "",
                    welcome_bonus_ttl_min: 43200,
                    // если админ (бэк вернёт promotions_config), подготовим поля редактора JSON
                    promotions_config_text: "{}",          // текстовое поле под JSON
                    promotions_config_mode: "merge",       // merge|replace
                });
                setError(
                    "Global settings are not initialized yet. Please fill in required fields and click Save."
                );
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [show]);

    const onChange = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setSuccess("");
        setError("");
    };

    // payload только с изменёнными полями
    const payload = useMemo(() => {
        if (!form) return null;
        const out = {};
        const keys = [...INT_FIELDS, ...BOOL_FIELDS, ...STR_FIELDS];
        for (const k of keys) {
            const newVal = normalizeForPayload(k, form[k]);
            const oldVal = orig ? normalizeForPayload(k, orig[k]) : undefined;
            if (!orig) {
                // первичное создание: шлём непустые (и update_active как есть)
                if (k === "update_active") out[k] = !!newVal;
                else if (newVal !== null && newVal !== "" && newVal !== undefined)
                    out[k] = newVal;
            } else if (!isEqualLoose(newVal, oldVal)) {
                out[k] = newVal;
            }
        }
        return out;
    }, [form, orig]);

    const handleSave = async () => {
        if (!form) return;
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            let payloadToSend = { ...(payload || {}) };

            // если есть редактор промо и что-то поменялось — упакуем
            if (typeof form.promotions_config_text === 'string') {
                const origText = JSON.stringify(orig?.promotions_config ?? {}, null, 2);
                const newText = form.promotions_config_text?.trim() ?? '';
                if (newText !== origText) {
                    try {
                        const parsed = newText ? JSON.parse(newText) : {};
                        payloadToSend.promotions_config = parsed;
                        if (form.promotions_config_mode === 'replace') {
                            payloadToSend.promotions_config_mode = 'replace';
                        }
                    } catch (e) {
                        setSaving(false);
                        return setError("Promotions JSON is invalid. Please fix and try again.");
                    }
                }
            }

            const saved = await updateGlobalSettings(payloadToSend);
            setOrig(saved);
            setForm(toFormState(saved));
            setSuccess(getSettingsSaveMismatch(payloadToSend, saved) || "Settings saved successfully.");
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };


    const handleStart = async () => {
        await saveWithPatch({ update_active: true });
    };

    const handleFinish = async () => {
        await saveWithPatch({ update_active: false });
    };

    const saveWithPatch = async (patch) => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            let payloadToSend = { ...(payload || {}), ...patch };

            if (typeof form?.promotions_config_text === 'string') {
                const origText = JSON.stringify(orig?.promotions_config ?? {}, null, 2);
                const newText = form.promotions_config_text?.trim() ?? '';
                if (newText !== origText) {
                    try {
                        const parsed = newText ? JSON.parse(newText) : {};
                        payloadToSend.promotions_config = parsed;
                        if (form.promotions_config_mode === 'replace') {
                            payloadToSend.promotions_config_mode = 'replace';
                        }
                    } catch (e) {
                        setSaving(false);
                        return setError("Promotions JSON is invalid. Please fix and try again.");
                    }
                }
            }

            const saved = await updateGlobalSettings(payloadToSend);
            setOrig(saved);
            setForm(toFormState(saved));
            setSuccess(
                getSettingsSaveMismatch(payloadToSend, saved) ||
                (patch.update_active ? "Maintenance announcement enabled." : "Maintenance announcement disabled.")
            );
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to update settings.");
        } finally {
            setSaving(false);
        }
    };


    return (
        <Modal show={show} onHide={handleClose} top size={"lg"}>
            <Modal.Header closeButton className="py-2">
                <Modal.Title className="fs-6">
                    Global Settings {form?.update_active ? (
                    <Badge bg="danger" className="ms-2">Maintenance ON</Badge>
                ) : (
                    <Badge bg="secondary" className="ms-2">Maintenance OFF</Badge>
                )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="small">
                {loading || !form ? (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" /> <span className="text-muted">Loading…</span>
                    </div>
                ) : (
                    <>


                        {/* Maintenance section */}
                        <Row className="g-2">
                            <Col xs={12}>
                                <Form.Check
                                    type="switch"
                                    id="update_active"
                                    label="Maintenance announcement active"
                                    checked={!!form.update_active}
                                    onChange={(e) => onChange("update_active", e.target.checked)}
                                />
                            </Col>
                            <Col md={4} xs={12}>
                                <Form.Group controlId="updateDuration" className="mb-3">
                                    <Form.Label>Maintenance starts in</Form.Label>
                                    <Form.Select
                                        value={form.update_duration_minutes != null ? String(form.update_duration_minutes) : ''}
                                        onChange={(e) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                update_duration_minutes: e.target.value ? parseInt(e.target.value, 10) : null,
                                            }))
                                        }
                                        size="sm"
                                    >
                                        <option value="">Select start delay...</option>
                                        <option value="10">10 minutes</option>
                                        <option value="30">30 minutes</option>
                                        <option value="60">1 hour</option>
                                        <option value="180">3 hours</option>
                                        <option value="360">6 hours</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Used only if “Active” is ON; countdown starts from the last save.
                                    </Form.Text>
                                </Form.Group>

                            </Col>
                            <Col md={8} xs={12}>
                                <Form.Label className="mb-1">Message</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    value={form.update_message}
                                    onChange={(e) => onChange("update_message", e.target.value)}
                                />
                            </Col>

                        </Row>

                        <hr className="my-2" />

                        {/* Limits / caps */}
                        <Row className="g-2">
                            <Col xs={12}>
                                <div className="fw-semibold mb-1">Limits & Caps</div>
                            </Col>

                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Free: Max Active</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.free_user_max_active_ass}
                                    onChange={(e) =>
                                        onChange("free_user_max_active_ass", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Essential: Max Active</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.essential_user_max_active_ass}
                                    onChange={(e) =>
                                        onChange("essential_user_max_active_ass", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Premium: Max Active</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.premium_user_max_active_ass}
                                    onChange={(e) =>
                                        onChange("premium_user_max_active_ass", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Maximum: Max Active</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.maximum_user_max_active_ass}
                                    onChange={(e) =>
                                        onChange("maximum_user_max_active_ass", e.target.value)
                                    }
                                />
                            </Col>

                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Max per 10 days</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.max_assignments_per_10_days}
                                    onChange={(e) =>
                                        onChange("max_assignments_per_10_days", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Max per 30 days</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.max_assignments_per_30_days}
                                    onChange={(e) =>
                                        onChange("max_assignments_per_30_days", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Max Reported</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.max_reported_assignments}
                                    onChange={(e) =>
                                        onChange("max_reported_assignments", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={3} xs={6}>
                                <Form.Label className="mb-1">Max Reading</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.max_reading_assignments}
                                    onChange={(e) =>
                                        onChange("max_reading_assignments", e.target.value)
                                    }
                                />
                            </Col>

                            <Col md={4} xs={12}>
                                <Form.Label className="mb-1">
                                    Max Assignments per Book (to post)
                                </Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.max_assignments_to_post_per_book}
                                    onChange={(e) =>
                                        onChange("max_assignments_to_post_per_book", e.target.value)
                                    }
                                />
                            </Col>
                            <Col md={4} xs={12}>
                                <Form.Label className="mb-1">Welcome Bonus TTL (min)</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={form.welcome_bonus_ttl_min}
                                    onChange={(e) => onChange("welcome_bonus_ttl_min", e.target.value)}
                                />
                            </Col>

                        </Row>
                    </>
                )}
                {/* Staff Message (Dashboard) */}
                <Card className="border border-secondary mb-2 mt-2">
                    <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <Form.Check
                                type="switch"
                                id="staff_message_active"
                                label="Staff Message active"
                                checked={!!form?.staff_message_active}
                                onChange={(e) => onChange("staff_message_active", e.target.checked)}
                            />
                            <Form.Check
                                type="switch"
                                id="dashboard_rdp_access_visible"
                                label="Show Shared Windows RDP"
                                checked={!!form?.dashboard_rdp_access_visible}
                                onChange={(e) => onChange("dashboard_rdp_access_visible", e.target.checked)}
                            />
                        </div>

                        <Form.Label className="mb-1 mt-2">Dashboard Staff Message</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            size="sm"
                            className="mb-1"
                            value={form?.staff_message_dashboard}
                            onChange={(e) => onChange("staff_message_dashboard", e.target.value)}
                            placeholder="Staff Message Here"
                        />

                        <div className="text-muted small">
                            This message appears on the user dashboard when <strong>Staff Message active</strong> is ON.
                            Shared Windows RDP appears only when <strong>Show Shared Windows RDP</strong> is ON.
                        </div>
                    </Card.Body>
                </Card>
                <Card className="border border-info mb-2 mt-2">
                    <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <div className="fw-semibold">SPA Update Notice</div>
                            <Form.Check
                                type="switch"
                                id="spa_update_notice_active"
                                label="Active"
                                checked={!!form?.spa_update_notice_active}
                                onChange={(e) => onChange("spa_update_notice_active", e.target.checked)}
                            />
                        </div>
                        <Row className="g-2">
                            <Col md={4} xs={12}>
                                <Form.Label className="mb-1">Version / Build key</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={form?.spa_update_notice_version || ""}
                                    onChange={(e) => onChange("spa_update_notice_version", e.target.value)}
                                    placeholder="2026-07-05-01"
                                />
                            </Col>
                            <Col md={4} xs={12}>
                                <Form.Label className="mb-1">Display mode</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={form?.spa_update_notice_mode || "modal"}
                                    onChange={(e) => onChange("spa_update_notice_mode", e.target.value)}
                                >
                                    <option value="green_line">Green line</option>
                                    <option value="modal">Modal</option>
                                    <option value="book_modal">Modal for books</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12}>
                                <Form.Label className="mb-1">Message</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    value={form?.spa_update_notice_message || ""}
                                    onChange={(e) => onChange("spa_update_notice_message", e.target.value)}
                                    placeholder="Virtalibry has just been updated. Refresh this page to continue with the latest version."
                                />
                            </Col>
                        </Row>
                        <div className="text-muted small mt-1">
                            Users see this when the Version / Build key differs from local storage.
                            The SPA checks this setting every 5 minutes.
                        </div>
                    </Card.Body>
                </Card>
                {typeof form?.promotions_config_text === 'string' && (
                    <Card className="border border-success mb-2 mt-2">
                        <Card.Body className="py-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="fw-semibold">Promotions (JSON)</div>
                                <Form.Select
                                    size="sm"
                                    style={{ maxWidth: 180 }}
                                    value={form.promotions_config_mode}
                                    onChange={(e) => onChange('promotions_config_mode', e.target.value)}
                                >
                                    <option value="merge">Merge (default)</option>
                                    <option value="replace">Replace</option>
                                </Form.Select>
                            </div>

                            <Form.Control
                                as="textarea"
                                rows={8}
                                size="sm"
                                value={form.promotions_config_text}
                                onChange={(e) => onChange('promotions_config_text', e.target.value)}
                                placeholder="{ /* JSON */ }"
                            />
                            <div className="text-muted small mt-1">
                                Admin-only. Saved to <code>promotions_config</code> (JSONB). Invalid JSON won’t be saved.
                            </div>
                        </Card.Body>
                    </Card>
                )}
                {error && (
                    <Alert
                        variant="danger"
                        className="py-2"
                        onClose={() => setError("")}
                        dismissible
                    >
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert
                        variant="success"
                        className="py-2"
                        onClose={() => setSuccess("")}
                        dismissible
                    >
                        {success}
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer className="py-2">
                <Button variant="secondary" size="sm" onClick={handleClose} disabled={saving}>
                    Close
                </Button>

                {form && form.update_active ? (
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={handleFinish}
                        disabled={saving}
                    >
                        {saving ? "Updating..." : "Disable Announcement"}
                    </Button>
                ) : (
                    <Button
                        variant="warning"
                        size="sm"
                        onClick={handleStart}
                        disabled={saving}
                    >
                        {saving ? "Updating..." : "Enable Announcement"}
                    </Button>
                )}

                <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Settings"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MaintenanceModal;

/* ===== helpers ===== */

function toFormState(data = {}) {
    const state = {
        // числа/булевые/строки из списков полей
        ...Object.fromEntries(INT_FIELDS.map((k) => [k, toUIInt(data[k])])),
        ...Object.fromEntries(BOOL_FIELDS.map((k) => [k, !!data[k]])),
        ...Object.fromEntries(STR_FIELDS.map((k) => [k, data[k] ?? ""])),

        // служебные вычисляемые из бэка
    };

    // Админ-вид: если бэк прислал promotions_config — показываем редактор JSON
    if (Object.prototype.hasOwnProperty.call(data, "promotions_config")) {
        state.promotions_config_text = JSON.stringify(data.promotions_config ?? {}, null, 2);
        state.promotions_config_mode = "merge"; // по умолчанию merge
    }

    return state;
}

function toUIInt(v) {
    if (v === null || v === undefined) return "";
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
    const n = Number(v);
    return Number.isFinite(n) ? String(Math.trunc(n)) : "";
}

function normalizeForPayload(key, val) {
    if (INT_FIELDS.includes(key)) {
        if (val === "" || val === null || val === undefined) return null;
        const n = Number(val);
        return Number.isFinite(n) ? Math.trunc(n) : null;
    }
    if (BOOL_FIELDS.includes(key)) return !!val;
    if (STR_FIELDS.includes(key)) return String(val ?? "").trim();
    return val;
}

function isEqualLoose(a, b) {
    if (typeof a === "number" || typeof b === "number") {
        return Number(a) === Number(b);
    }
    return String(a ?? "") === String(b ?? "");
}

function getSettingsSaveMismatch(payload = {}, saved = {}) {
    if (
        Object.prototype.hasOwnProperty.call(payload, "dashboard_rdp_access_visible") &&
        !!payload.dashboard_rdp_access_visible !== !!saved.dashboard_rdp_access_visible
    ) {
        return "Settings saved, but Show Shared Windows RDP did not come back from the server yet. Please reload Global Settings.";
    }

    if (
        Object.prototype.hasOwnProperty.call(payload, "spa_update_notice_active") &&
        !!payload.spa_update_notice_active !== !!saved.spa_update_notice_active
    ) {
        return "Settings saved, but SPA Update Notice did not come back from the server yet. Please reload Global Settings.";
    }

    return "";
}
